/* eslint-disable formatjs/enforce-description */
import type { FunctionComponent, ReactNode } from 'react';
import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import { Analytics } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { asMoney } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';

import { PlanComparisonContext } from './PlanComparisonContext';

import styles from './PlanComparisonTotals.less';

export interface TotalsCellProps {
  monthlyPrice?: number;
  annualPrice?: number;
  isMonthly: boolean;
  monthlyCollaborators?: number;
  annualCollaborators?: number;
}

export const getTotalsText = ({
  isMonthly,
  annualPrice = 0,
  monthlyPrice = 0,
}: TotalsCellProps) => {
  const monthlyRatePerYear = monthlyPrice ? monthlyPrice * 12 : 0;
  return isMonthly ? (
    <p className={classnames(styles.text, styles.total)}>
      <FormattedMessage
        id="templates.billing_page_one.monthly-total-text"
        defaultMessage="{monthlyPrice} total per month, totaling {annualPrice} annually"
        values={{
          monthlyPrice: (
            <span key={monthlyPrice}>
              <b className={styles.price}>
                {intl
                  .formatMessage(
                    {
                      id: 'templates.billing_page_one.amount-usd',
                      defaultMessage: '${amount} USD',
                    },
                    {
                      amount: asMoney(monthlyPrice).replace(/\.00$/, ''),
                    },
                  )
                  .replace(/\s/g, ' ')}
              </b>
              <br />
            </span>
          ),
          annualPrice: intl
            .formatMessage(
              {
                id: 'templates.billing_page_one.amount-usd',
                defaultMessage: '${amount} USD',
              },
              {
                amount: asMoney(monthlyRatePerYear).replace(/\.00$/, ''),
              },
            )
            .replace(/\s/g, ' '),
        }}
      />
    </p>
  ) : (
    <p className={classnames(styles.text, styles.total)}>
      <FormattedMessage
        id="templates.billing_page_one.annual-total-text"
        defaultMessage="{annualPrice} paid annually"
        values={{
          annualPrice: (
            <span key={annualPrice}>
              <b className={styles.price}>
                {intl
                  .formatMessage(
                    {
                      id: 'templates.billing_page_one.amount-usd',
                      defaultMessage: '${amount} USD',
                    },
                    {
                      amount: asMoney(annualPrice).replace(/\.00$/, ''),
                    },
                  )
                  .replace(/\s/g, ' ')}
              </b>
              <br />
            </span>
          ),
        }}
      />
    </p>
  );
};

const TotalsCell: FunctionComponent<TotalsCellProps> = ({
  monthlyPrice,
  annualPrice,
  isMonthly,
}) => {
  const totalsText = getTotalsText({
    monthlyPrice,
    annualPrice,
    isMonthly,
  });
  return (
    <td className={classnames(styles.cell, styles.cellTotal)}>{totalsText}</td>
  );
};

interface OwnProps {
  teamName: ReactNode;
  isMonthly: boolean;
  numOfMembers?: number;
  teamId: string;
}

export const PlanComparisonTotals: FunctionComponent<OwnProps> = ({
  teamName,
  isMonthly,
  numOfMembers = 1,
  teamId,
}) => {
  const { billingQuote, isModal } = useContext(PlanComparisonContext);

  const standardMonthly = billingQuote?.standardPrices?.monthly;
  const standardAnnual = billingQuote?.standardPrices?.annual;
  const businessMonthly = billingQuote?.businessPrices?.monthly;
  const businessAnnual = billingQuote?.businessPrices?.annual;

  const onClickContactUs = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'contactUsButton',
      source: isModal ? 'planComparisonModal' : 'planComparisonSection',
      containers: {
        organization: {
          id: teamId,
        },
      },
    });
  };

  return (
    <tr>
      <td key="empty-cell" className={styles.noCell}>
        {}
      </td>
      <td
        key="header-cell"
        className={classnames(styles.cell, styles.cellHeader)}
      >
        <p className={styles.text}>
          <FormattedMessage
            id="templates.billing_page_one.plan-comparison-totals-row-header"
            defaultMessage="For all members and billable guests in {teamName}, you'd pay:"
            values={{ teamName }}
          />
        </p>
      </td>
      <TotalsCell
        key="standard-totals"
        monthlyPrice={standardMonthly?.subTotal}
        annualPrice={standardAnnual?.subTotal}
        isMonthly={isMonthly}
      />
      <TotalsCell
        key="business-totals"
        monthlyPrice={businessMonthly?.subTotal}
        annualPrice={businessAnnual?.subTotal}
        isMonthly={isMonthly}
      />
      <td className={classnames(styles.cell, styles.contactBtn)}>
        <Button
          onClick={onClickContactUs}
          key="enterprise-contact-us"
          linkTarget="_blank"
          href={'/enterprise#contact'}
        >
          <FormattedMessage
            id="templates.billing_page_one.contact-sales-repackaging-gtm"
            defaultMessage="Contact sales"
          />
        </Button>
      </td>
    </tr>
  );
};
